/**
 * Wire protocol for breakout rooms: the `breakoutState` room custom property,
 * Photon event 57 command payloads, breakout level ids, and the pure helpers
 * the orchestration layer needs (auto-split, timer/grace decisions, refresh
 * rejoin). Pure and window-free (like ballProtocol.ts) so it unit-tests in
 * node.
 *
 * Sync model (see docs/breakout-rooms.md):
 * - Only `manageBreakouts` actors write state: property FIRST, then event 57
 *   (the worldSession convention). Receivers re-read the property on every
 *   command, so a missed event self-heals at the next hydration point.
 * - Membership truth is the existing `currentLevel` actor property - being in
 *   a breakout IS being in its `breakout:<sessionId>:<roomId>` level. The
 *   `assignments` map records host INTENT (who was told to go where); live
 *   occupancy is always derived from the roster, never from this state.
 * - Countdown displays render locally from endsAt/closingAt (clock skew only
 *   skews the display); the authoritative close is a command fired by the
 *   lowest-actorNr manager (shouldFireTimerClose / shouldFinalizeClose).
 *
 * Decoders validate everything: the property and events are peer-writable and
 * never trusted raw. Sender identity always comes from the server-stamped
 * onEvent actorNr, and receivers additionally gate event 57 on the sender's
 * `manageBreakouts` capability.
 */

import { CONFIG } from '../config/client.config.js';
import { isLayoutLevelId } from '../config/levels.js';

/** Manager -> room (or -> one target for 'assign'): breakout command. */
export const BREAKOUT_EVENT_CODE = CONFIG.BREAKOUT.EVENT_CODE;
/** Room custom property holding the full breakout state (JSON string). */
export const BREAKOUT_STATE_ROOM_PROP = CONFIG.BREAKOUT.ROOM_PROP;
export const MAX_ROOMS = CONFIG.BREAKOUT.MAX_ROOMS;
export const MAX_NAME_LENGTH = CONFIG.BREAKOUT.MAX_NAME_LENGTH;
/** Grace between close_start and everyone auto-returning to main. */
export const GRACE_MS = CONFIG.BREAKOUT.GRACE_MS;
/** Warning banner shown to an assigned guest before the forced move. */
export const PRE_MOVE_MS = CONFIG.BREAKOUT.PRE_MOVE_MS;
/** Lowest manager finalizes {active:false} this long after grace expiry. */
export const CLOSE_FINALIZE_LAG_MS = 2000;

const LEVEL_PREFIX = 'breakout:';
const MODES = ['assign', 'auto', 'choose'] as const;
const ACTIONS = ['open', 'update', 'assign', 'close_start', 'close_cancel', 'closed'] as const;
/** sessionId / roomId charset - short, URL/key-safe, lowercase. */
const ID_RE = /^[a-z0-9-]{1,32}$/;
const MAX_STATE_BYTES = 8192;
const MAX_LEVEL_ID_LENGTH = 120;
export const STANDARD_BREAKOUT_TEMPLATE_ID = 'standard-lounge';
const MAX_TEMPLATE_NAME_LENGTH = 120;

export type BreakoutMode = (typeof MODES)[number];
export type BreakoutAction = (typeof ACTIONS)[number];

export interface BreakoutRoom {
  id: string;
  name: string;
  /** standard-lounge or a curated layout:<uuid> */
  templateId: string;
  /** display label captured when rooms open */
  templateName: string;
}

export interface BreakoutState {
  active: true;
  /** fresh per "Open rooms"; namespaces level ids */
  sessionId: string;
  mode: BreakoutMode;
  /** the level breakouts opened from; return target */
  mainLevelId: string;
  rooms: BreakoutRoom[];
  /** actorNr (string) -> roomId */
  assignments: Record<string, string>;
  /** optional timer target (epoch ms) */
  endsAt: number | null;
  /** grace start (epoch ms); null = not closing */
  closingAt: number | null;
  /** actorNr, informational */
  openedBy: number;
  /** monotonic revision; readers ignore regressions */
  rev: number;
}

export interface InactiveBreakoutState {
  active: false;
}

export type AnyBreakoutState = BreakoutState | InactiveBreakoutState;

export interface BreakoutCommand {
  action: BreakoutAction;
  sessionId: string;
  targetActorNr?: number;
  roomId?: string | null;
}

function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

function isValidId(id: unknown): id is string {
  return typeof id === 'string' && ID_RE.test(id);
}

export function isValidBreakoutTemplateId(id: unknown): boolean {
  return id === STANDARD_BREAKOUT_TEMPLATE_ID || isLayoutLevelId(id);
}

function normalizeTemplateName(name: unknown): string | null {
  if (typeof name !== 'string') return null;
  const trimmed = name.trim().slice(0, MAX_TEMPLATE_NAME_LENGTH);
  return trimmed || null;
}

/** Mint a fresh breakout session id (8 base36 chars). */
export function mintBreakoutSessionId(rng: () => number = Math.random): string {
  let out = '';
  while (out.length < 8) out += rng().toString(36).slice(2);
  return out.slice(0, 8);
}

export function makeBreakoutLevelId(sessionId: string, roomId: string): string {
  return `${LEVEL_PREFIX}${sessionId}:${roomId}`;
}

export function isBreakoutLevelId(levelId: unknown): levelId is string {
  return typeof levelId === 'string' && levelId.startsWith(LEVEL_PREFIX);
}

export function parseBreakoutLevelId(
  levelId: unknown,
): { sessionId: string; roomId: string } | null {
  if (typeof levelId !== 'string' || levelId.length > MAX_LEVEL_ID_LENGTH) return null;
  if (!levelId.startsWith(LEVEL_PREFIX)) return null;
  const parts = levelId.slice(LEVEL_PREFIX.length).split(':');
  if (parts.length !== 2) return null;
  const [sessionId, roomId] = parts;
  if (!isValidId(sessionId) || !isValidId(roomId)) return null;
  return { sessionId, roomId };
}

/** Normalize a proposed room name; null when unusable. */
export function normalizeRoomName(name: unknown): string | null {
  if (typeof name !== 'string') return null;
  const trimmed = name.trim().slice(0, MAX_NAME_LENGTH);
  return trimmed.length ? trimmed : null;
}

/**
 * Encode state for the room property. Callers pass a fully-formed state
 * (build it via decode-mutate-encode or the writer in multiplayer.js).
 */
export function encodeBreakoutState(state: AnyBreakoutState): string {
  return JSON.stringify({ v: 1, ...state });
}

/**
 * Decode + validate the `breakoutState` room property.
 * Returns:
 * - a validated {@link BreakoutState} when active,
 * - `{active: false}` for a well-formed inactive/closed state,
 * - `null` for anything malformed (peer-writable - fail closed to "no
 *   breakouts", never to a partially-trusted state).
 */
export function decodeBreakoutState(raw: unknown): AnyBreakoutState | null {
  if (raw == null) return null;
  if (typeof raw !== 'string' || raw.length > MAX_STATE_BYTES) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const record = parsed as Record<string, unknown>;
  if (record.v !== 1) return null;
  if (record.active !== true) return { active: false };

  const { sessionId, mode, mainLevelId, rooms, assignments, endsAt, closingAt, openedBy, rev } =
    record;
  if (!isValidId(sessionId)) return null;
  if (!MODES.includes(mode as BreakoutMode)) return null;
  if (
    typeof mainLevelId !== 'string' ||
    !mainLevelId.length ||
    mainLevelId.length > MAX_LEVEL_ID_LENGTH ||
    isBreakoutLevelId(mainLevelId)
  ) {
    return null;
  }
  if (!Array.isArray(rooms) || rooms.length < 1 || rooms.length > MAX_ROOMS) return null;
  const seenRoomIds = new Set<string>();
  const cleanRooms: BreakoutRoom[] = [];
  for (const room of rooms) {
    if (!room || typeof room !== 'object') return null;
    if (!isValidId(room.id) || seenRoomIds.has(room.id)) return null;
    const name = normalizeRoomName(room.name);
    if (!name) return null;
    seenRoomIds.add(room.id);
    // Additive v1 compatibility: breakout state written before selectable
    // templates omitted both fields and always meant the generated lounge.
    const templateId = room.templateId ?? STANDARD_BREAKOUT_TEMPLATE_ID;
    if (!isValidBreakoutTemplateId(templateId)) return null;
    const templateName =
      templateId === STANDARD_BREAKOUT_TEMPLATE_ID
        ? 'Standard Lounge'
        : normalizeTemplateName(room.templateName);
    if (!templateName) return null;
    cleanRooms.push({ id: room.id, name, templateId, templateName });
  }
  if (!assignments || typeof assignments !== 'object' || Array.isArray(assignments)) return null;
  const cleanAssignments: Record<string, string> = {};
  for (const [actorKey, roomId] of Object.entries(assignments)) {
    if (!/^\d{1,10}$/.test(actorKey)) return null;
    // Tolerate (drop) entries pointing at unknown rooms rather than rejecting
    // the whole state - a racing edit must not blank every client's UI.
    if (typeof roomId === 'string' && seenRoomIds.has(roomId)) cleanAssignments[actorKey] = roomId;
  }
  if (endsAt !== null && endsAt !== undefined && !(isFiniteNumber(endsAt) && endsAt > 0))
    return null;
  if (
    closingAt !== null &&
    closingAt !== undefined &&
    !(isFiniteNumber(closingAt) && closingAt > 0)
  )
    return null;
  if (!Number.isInteger(openedBy) || (openedBy as number) < 1) return null;
  if (!Number.isInteger(rev) || (rev as number) < 0) return null;

  return {
    active: true,
    sessionId,
    mode: mode as BreakoutMode,
    mainLevelId,
    rooms: cleanRooms,
    assignments: cleanAssignments,
    endsAt: isFiniteNumber(endsAt) ? endsAt : null,
    closingAt: isFiniteNumber(closingAt) ? closingAt : null,
    openedBy: openedBy as number,
    rev: rev as number,
  };
}

export function encodeBreakoutCommand(
  sessionId: string,
  action: Exclude<BreakoutAction, 'assign'>,
): { v: 1; sessionId: string; action: string } {
  return { v: 1, sessionId, action };
}

/** @param roomId - null = pull the target back to main */
export function encodeAssignCommand(
  sessionId: string,
  targetActorNr: number,
  roomId: string | null,
): { v: 1; sessionId: string; action: 'assign'; targetActorNr: number; roomId: string | null } {
  return { v: 1, sessionId, action: 'assign', targetActorNr, roomId };
}

/** Decode + validate an event 57 payload. */
export function decodeBreakoutCommand(content: unknown): BreakoutCommand | null {
  if (!content || typeof content !== 'object') return null;
  const { v, sessionId, action } = content as {
    v?: unknown;
    sessionId?: unknown;
    action?: unknown;
  };
  if (v !== 1 || !isValidId(sessionId) || !ACTIONS.includes(action as BreakoutAction)) return null;
  if (action !== 'assign') return { action: action as BreakoutAction, sessionId };
  const { targetActorNr, roomId } = content as { targetActorNr?: unknown; roomId?: unknown };
  if (!Number.isInteger(targetActorNr) || (targetActorNr as number) < 1) return null;
  if (roomId !== null && !isValidId(roomId)) return null;
  return {
    action,
    sessionId,
    targetActorNr: targetActorNr as number,
    roomId: roomId as string | null,
  };
}

/**
 * Round-robin auto-split over a shuffled copy - room sizes never differ by
 * more than one. Managers are excluded by the caller (they stay unassigned).
 * @returns actorNr (string) -> roomId
 */
export function autoSplit(
  actorNrs: number[],
  roomIds: string[],
  rng: () => number = Math.random,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!roomIds.length) return out;
  const shuffled = [...actorNrs];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j] as number, shuffled[i] as number];
  }
  shuffled.forEach((actorNr, i) => {
    out[String(actorNr)] = roomIds[i % roomIds.length] as string;
  });
  return out;
}

/**
 * Should the lowest-actorNr manager fire close_start because the timer hit?
 * Idempotent: false once closingAt is set.
 */
export function shouldFireTimerClose(state: AnyBreakoutState | null, nowMs: number): boolean {
  return !!(
    state &&
    state.active &&
    state.endsAt != null &&
    state.closingAt == null &&
    nowMs >= state.endsAt
  );
}

/**
 * Should THIS client return to main? True while in a breakout level of the
 * current session once grace expired or the session went inactive/foreign.
 */
export function shouldSelfReturn(
  state: AnyBreakoutState | null,
  currentLevelId: string | null | undefined,
  nowMs: number,
): boolean {
  const parsed = parseBreakoutLevelId(currentLevelId);
  if (!parsed) return false;
  if (!state || !state.active) return true;
  if (state.sessionId !== parsed.sessionId) return true; // stale lounge from a prior session
  if (!state.rooms.some((r) => r.id === parsed.roomId)) return true;
  return state.closingAt != null && nowMs >= state.closingAt;
}

/**
 * Should the lowest-actorNr manager finalize {active:false} + `closed`?
 * Lags grace expiry so self-returns land first.
 */
export function shouldFinalizeClose(state: AnyBreakoutState | null, nowMs: number): boolean {
  return !!(
    state &&
    state.active &&
    state.closingAt != null &&
    nowMs >= state.closingAt + CLOSE_FINALIZE_LAG_MS
  );
}

/**
 * Where should a refresh-er auto-rejoin? (sessionStorage carries
 * {sessionId, roomId} per tab - see docs/breakout-rooms.md.)
 * @param saved - parsed sessionStorage value
 * @returns breakout level id, or null to stay in main
 */
export function rejoinLevelId(state: AnyBreakoutState | null, saved: unknown): string | null {
  if (!state || !state.active || state.closingAt != null) return null;
  if (!saved || typeof saved !== 'object') return null;
  const { sessionId, roomId } = saved as { sessionId?: unknown; roomId?: unknown };
  if (sessionId !== state.sessionId || !isValidId(roomId)) return null;
  if (!state.rooms.some((r) => r.id === roomId)) return null;
  return makeBreakoutLevelId(state.sessionId, roomId);
}
